import React, { useEffect, useRef, useState } from 'react'
import { gql, useQuery } from '@apollo/client'
import {
  Alert, Box, Button, Collapse, IconButton, Table, TableBody, TableCell, TableHead,
  TablePagination, TableRow, Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import UndoOutlinedIcon from '@mui/icons-material/UndoOutlined'
import { getStripeRestaurantTransactions } from '../../apollo/queries'
import { Card, CardHeader, EmptyState, SkeletonRows, StatusPill, tableSx } from './ui'
import { color, dayLabel, focusRing, radius, usd } from './tokens'

const GET_TRANSACTIONS = gql`${getStripeRestaurantTransactions}`
const PAGE = 25

const cents = (v) => Math.round(Number(v || 0) * 100) / 100

const TYPE = { PLATFORM_DELIVERY: 'Delivery', RESTAURANT_DELIVERY: 'Delivery', PICKUP: 'Pickup' }
const SCENARIO = {
  PLATFORM_DELIVERY: 'Delivered by a RushmyFood driver',
  RESTAURANT_DELIVERY: 'Delivered by your driver',
  PICKUP: 'Pickup',
}

// Why part of a share was kept back, by the kind of balance it settled.
const WITHHELD_REASON = {
  DELIVERY_FALLBACK: 'a RushmyFood driver delivered it, after its delivery fee and tip had been credited to you',
  MANUAL: 'a balance adjustment',
  OTHER: 'a refund or adjustment',
}

function statusOf(t) {
  if (t.disputeStatus) return { tone: 'error', label: 'Disputed' }
  if (t.status === 'REFUNDED') return { tone: 'neutral', label: 'Refunded' }
  if (Number(t.refundedAmount) > 0) return { tone: 'warning', label: 'Partially refunded' }
  if (t.status === 'PENDING') return { tone: 'warning', label: 'Pending' }
  if (t.status === 'SETTLED') return { tone: 'success', label: 'Sent to you' }
  return { tone: 'neutral', label: t.status || '—' }
}

// "label …dotted leader… amount". Zero lines are hidden unless they are the
// panel's total, so a pickup order doesn't list a $0 delivery fee.
function MoneyRow({ label, hint, value, strong, tone, negative }) {
  if (!strong && (value == null || Math.abs(Number(value)) < 0.005)) return null
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, py: 0.45 }}>
      <Typography sx={{ fontSize: 14, color: strong ? color.text : color.muted, fontWeight: strong ? 600 : 400, minWidth: 0 }}>
        {label}
        {hint && <Typography component="span" sx={{ fontSize: 12, color: color.faint, ml: 0.6 }}>{hint}</Typography>}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 12, borderBottom: `1px dotted ${color.border}`, transform: 'translateY(-4px)' }} />
      <Typography sx={{ fontSize: 14, fontWeight: strong ? 700 : 500, color: tone || color.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {negative ? `−${usd(Math.abs(value))}` : usd(value)}
      </Typography>
    </Box>
  )
}

/**
 * One order, itemized: what the customer paid, how that became the
 * restaurant's share, what RushmyFood kept, and what actually reached the
 * restaurant. The panels reconcile to the customer total, so a share that
 * looks short (debt kept back, a refund) always has a named line explaining it.
 */
function TxnDetail({ t }) {
  const rb = t.restaurantBreakdown || {}
  const kb = t.rushmyfoodBreakdown || {}
  const total = Number(t.customerTotal) || 0
  const share = Number(t.restaurantShare) || 0
  const wallet = Number(t.walletCredit) || 0
  const commission = cents(Number(t.subtotal || 0) - Number(rb.subtotalShare || 0))
  // Whatever the customer paid that no named line covers is a tip for the
  // restaurant's staff (the engine books it to the restaurant as "other").
  const staffTip = cents(total - (Number(t.subtotal || 0) - wallet) - Number(t.deliveryFee || 0) -
    Number(t.deliveryTip || 0) - Number(t.tax || 0) - Number(t.serviceFee || 0))
  const rushmyfood = cents(total - share)
  const withheld = Number(t.debtRecovered) || 0
  const sent = t.status === 'SETTLED' || Number(t.sentToRestaurant) > 0
  const platformDelivered = t.scenario === 'PLATFORM_DELIVERY'
  // The card processing fee taken from the share, at the rate the order was
  // placed with. Orders placed before the fee existed have none.
  const processingFee = Number(t.processingFee) || 0
  const rate = t.processingFeePercent != null
    ? `${Number(t.processingFeePercent)}% + ${usd(t.processingFeeFixed || 0)}`
    : null

  const panel = {
    flex: '1 1 250px',
    minWidth: 0,
    borderRadius: '10px',
    p: 2,
    bgcolor: color.surface,
    border: `1px solid ${color.border}`,
  }
  const heading = (text, tone) => (
    <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: tone, mb: 0.75 }}>
      {text}
    </Typography>
  )

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 2.5, bgcolor: color.subtle }}>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1.75 }}>
        {SCENARIO[t.scenario] && <StatusPill tone="neutral">{SCENARIO[t.scenario]}</StatusPill>}
        {t.commissionRate != null && <StatusPill tone="neutral">{Number(t.commissionRate)}% commission</StatusPill>}
        {t.transferredAt && <StatusPill tone="success">Sent {new Date(t.transferredAt).toLocaleString()}</StatusPill>}
      </Box>

      {withheld > 0 && (
        <Box sx={{ mb: 1.75, px: 2, py: 1.5, borderRadius: '10px', bgcolor: color.warningSoft }}>
          <Typography sx={{ fontSize: 14, color: color.warningText, lineHeight: 1.55 }}>
            {usd(withheld)} of your {usd(share)} share was kept back to settle an earlier balance, so {usd(t.sentToRestaurant)} was
            sent to you.
            {(t.withheldFor || []).map((w, i) => (
              <span key={i}>
                {' '}
                {usd(w.amount)} was for {w.orderNumber ? `order #${w.orderNumber}` : 'an earlier order'}:{' '}
                {WITHHELD_REASON[w.reason] || WITHHELD_REASON.OTHER}.
              </span>
            ))}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={panel}>
          {heading('Customer paid', color.muted)}
          <MoneyRow label="Items" value={t.subtotal} />
          <MoneyRow label="Paid with RushmyFood credit" hint="(you are still paid on it)" value={wallet} negative />
          <MoneyRow label="Delivery fee" value={t.deliveryFee} />
          <MoneyRow label="Driver tip" value={t.deliveryTip} />
          <MoneyRow label="Staff tip" value={staffTip > 0 ? staffTip : null} />
          <MoneyRow label="Tax" value={t.tax} />
          <MoneyRow label="Service fee" value={t.serviceFee} />
          <Box sx={{ borderTop: `1px solid ${color.border}`, mt: 0.75, pt: 0.75 }}>
            <MoneyRow label="Charged to card" value={total} strong />
          </Box>
          {Number(t.refundedAmount) > 0 && <MoneyRow label="Refunded since" value={t.refundedAmount} tone={color.error} negative />}
        </Box>

        <Box sx={{ ...panel, boxShadow: `inset 0 3px 0 ${color.success}` }}>
          {heading('You receive', color.success)}
          <MoneyRow label="Items" value={t.subtotal} />
          <MoneyRow label="Commission" hint={t.commissionRate != null ? `(${Number(t.commissionRate)}%)` : null} value={commission} negative />
          <MoneyRow label="Delivery fee" hint="(your driver)" value={rb.deliveryFee} />
          <MoneyRow label="Driver tip" hint="(your driver)" value={rb.deliveryTip} />
          <MoneyRow label="Tax" hint="(you pay it to the state)" value={rb.tax} />
          <MoneyRow label="Staff tip" value={rb.other} />
          <MoneyRow label="Processing fee" hint={rate ? `(${rate} of the card charge)` : null} value={processingFee || null} negative />
          <MoneyRow label="Store credit spent" hint="(your earlier refund to this customer)" value={Math.abs(Number(rb.storeCredit)) || null} negative />
          <MoneyRow label="Covered by RushmyFood" value={rb.coveredByPlatform} />
          <Box sx={{ borderTop: `1px solid ${color.border}`, mt: 0.75, pt: 0.75 }}>
            <MoneyRow label="Your share" value={share} strong tone={color.success} />
          </Box>
          {(t.withheldFor || []).length > 0
            ? t.withheldFor.map((w, i) => (
              <MoneyRow key={i} label="Kept back" hint={w.orderNumber ? `(for order #${w.orderNumber})` : null} value={w.amount} tone={color.warningText} negative />
            ))
            : <MoneyRow label="Kept back" hint="(earlier balance)" value={withheld} tone={color.warningText} negative />}
          <Box sx={{ borderTop: `1px solid ${color.border}`, mt: 0.75, pt: 0.75 }}>
            {sent
              ? <MoneyRow label="Sent to your Stripe balance" value={t.sentToRestaurant} strong tone={color.success} />
              : <MoneyRow label="Sent once the order is finished" value={share} strong tone={color.muted} />}
          </Box>
          {Number(t.takenBack) > 0 && <MoneyRow label="Taken back for refunds" value={t.takenBack} tone={color.error} negative />}
        </Box>

        <Box sx={{ ...panel, boxShadow: `inset 0 3px 0 ${color.accent}` }}>
          {heading('RushmyFood keeps', color.accent)}
          <MoneyRow label="Commission" hint={t.commissionRate != null ? `(${Number(t.commissionRate)}% of items)` : null} value={kb.commission} />
          <MoneyRow label="Service fee" hint="(paid by the customer)" value={kb.flatFee} />
          <MoneyRow label="Processing fee" hint="(from your share)" value={kb.processingFee} />
          <MoneyRow label="Delivery fee" hint={platformDelivered ? '(RushmyFood driver)' : null} value={kb.deliveryFee} />
          <MoneyRow label="Driver tip" hint="(passed to the driver)" value={kb.deliveryTip} />
          <MoneyRow label="Tax" hint="(RushmyFood pays it to the state)" value={kb.tax} />
          <MoneyRow label="RushmyFood credit used" hint="(RushmyFood pays you for it)" value={kb.walletCredit} negative />
          <MoneyRow label="Covered for you" value={kb.coveredForRestaurant} negative />
          <Box sx={{ borderTop: `1px solid ${color.border}`, mt: 0.75, pt: 0.75 }}>
            <MoneyRow label="Total" value={rushmyfood} strong tone={color.accent} />
          </Box>
        </Box>
      </Box>

      <Typography sx={{ fontSize: 13, color: color.muted, mt: 1.5 }}>
        {usd(share)} your share + {usd(rushmyfood)} RushmyFood = {usd(total)} the customer paid.
        {processingFee > 0 && ` Your share is after the ${usd(processingFee)} card processing fee.`}
      </Typography>
    </Box>
  )
}

/**
 * The restaurant's Stripe orders (or, with refundedOnly, only the refunded
 * ones). Each row opens into its full breakdown.
 */
export default function TransactionsSection({ restaurantId, refundedOnly = false, onRefund, reloadKey }) {
  const [page, setPage] = useState(0)
  const [openId, setOpenId] = useState(null)
  const { data, loading, error, refetch } = useQuery(GET_TRANSACTIONS, {
    variables: { restaurantId, refundedOnly, limit: PAGE, offset: page * PAGE },
    fetchPolicy: 'cache-and-network',
  })
  useEffect(() => {
    if (reloadKey) refetch()
  }, [reloadKey])

  const rows = data?.stripeRestaurantTransactions?.rows || []
  const count = data?.stripeRestaurantTransactions?.count || 0
  // The actions column (Refund) only when the caller can refund. Restaurants
  // can't since 2026-09-30: refunds are RushMyFood's call.
  const withActions = !refundedOnly && !!onRefund
  const columns = withActions ? 8 : 7
  const firstLoad = loading && !data

  // On a phone the table is wider than the card and scrolls sideways. The
  // breakdown under a row must not scroll with it — it is sized to the visible
  // width and pinned to the left, so all three panels stack in view.
  const scrollRef = useRef(null)
  const [visibleWidth, setVisibleWidth] = useState(null)
  const hasRows = rows.length > 0
  useEffect(() => {
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver(([entry]) => setVisibleWidth(Math.floor(entry.contentRect.width)))
    ro.observe(el)
    return () => ro.disconnect()
  }, [hasRows])

  return (
    <Card padded={false} sx={{ overflow: 'hidden' }}>
      <Box sx={{ px: { xs: 2.5, md: 3.5 }, pt: { xs: 2.5, md: 3 } }}>
        <CardHeader
          title={refundedOnly ? 'Refunds' : 'Transactions'}
          subtitle={refundedOnly
            ? 'Refunds on your orders, and what was taken back from your share.'
            : 'Every Stripe order and your share of it. Open a row for the full breakdown.'}
          sx={{ mb: 2 }}
        />
      </Box>

      {error && <Alert severity="error" sx={{ mx: 3, mb: 2 }}>{error.message}</Alert>}

      {!firstLoad && !error && rows.length === 0 ? (
        refundedOnly ? (
          <EmptyState icon={UndoOutlinedIcon} title="No refunds yet" body="Refunds you issue from Transactions show up here." />
        ) : (
          <EmptyState icon={ReceiptLongOutlinedIcon} title="No transactions yet" body="Orders paid by card through Stripe show up here as soon as the customer pays." />
        )
      ) : (
        <Box ref={scrollRef} sx={{ overflowX: 'auto' }}>
          <Table sx={{ ...tableSx, minWidth: refundedOnly ? 640 : 760 }} aria-label={refundedOnly ? 'Refunds' : 'Transactions'}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 48, pl: { xs: 1.5, md: 2.5 } }}><Box component="span" sx={visuallyHidden}>Details</Box></TableCell>
                <TableCell scope="col">Date</TableCell>
                <TableCell scope="col">Order</TableCell>
                {refundedOnly ? (
                  <>
                    <TableCell scope="col" align="right">Order total</TableCell>
                    <TableCell scope="col" align="right">Refunded</TableCell>
                    <TableCell scope="col" align="right">Taken back from you</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell scope="col">Type</TableCell>
                    <TableCell scope="col" align="right">Amount</TableCell>
                    <TableCell scope="col" align="right">Net to you</TableCell>
                  </>
                )}
                <TableCell scope="col">Status</TableCell>
                {withActions && <TableCell sx={{ pr: { xs: 1.5, md: 2.5 } }}><Box component="span" sx={visuallyHidden}>Actions</Box></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {firstLoad && <SkeletonRows rows={5} columns={columns} />}
              {rows.map((r) => {
                const open = openId === r.orderId
                const toggle = () => setOpenId(open ? null : r.orderId)
                const st = statusOf(r)
                const withheld = Number(r.debtRecovered) > 0
                return (
                  <React.Fragment key={r.orderId}>
                    <TableRow
                      hover
                      onClick={toggle}
                      sx={{ cursor: 'pointer', '& > td': { borderBottom: open ? 'none' : undefined }, '&:hover': { bgcolor: `${color.subtle} !important` } }}
                    >
                      <TableCell sx={{ width: 48, pl: { xs: 1.5, md: 2.5 }, pr: 0 }}>
                        <IconButton
                          size="small"
                          aria-label={open ? `Hide breakdown for order ${r.orderNumber}` : `Show breakdown for order ${r.orderNumber}`}
                          aria-expanded={open}
                          onClick={(e) => { e.stopPropagation(); toggle() }}
                          sx={focusRing}
                        >
                          <KeyboardArrowDownIcon fontSize="small" sx={{ color: color.muted, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 180ms ease' }} />
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap', color: `${color.muted} !important` }}>
                        {dayLabel(r.createdAt, { month: 'short', day: 'numeric', year: 'numeric' })}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>#{r.orderNumber || r.orderId}</TableCell>
                      {refundedOnly ? (
                        <>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{usd(r.customerTotal)}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{usd(r.refundedAmount)}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{Number(r.takenBack) > 0 ? usd(r.takenBack) : '—'}</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell sx={{ color: `${color.muted} !important` }}>{TYPE[r.scenario] || 'Order'}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{usd(r.customerTotal)}</TableCell>
                          <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            <Box sx={{ fontWeight: 600 }}>{usd(withheld ? r.sentToRestaurant : r.restaurantShare)}</Box>
                            {withheld && (
                              <Box sx={{ fontSize: 12, color: color.warningText }}>{usd(r.debtRecovered)} kept back</Box>
                            )}
                          </TableCell>
                        </>
                      )}
                      <TableCell><StatusPill tone={st.tone}>{st.label}</StatusPill></TableCell>
                      {withActions && (
                        <TableCell align="right" sx={{ pr: { xs: 1.5, md: 2.5 } }}>
                          {r.refundable > 0 && !r.disputeStatus && (
                            <Button
                              size="small"
                              onClick={(e) => { e.stopPropagation(); onRefund(r) }}
                              sx={{ textTransform: 'none', fontWeight: 600, color: color.accent, borderRadius: radius.control, ...focusRing }}
                            >
                              Refund
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={columns} sx={{ p: '0 !important', borderBottom: open ? `1px solid ${color.divider}` : 'none !important' }}>
                        <Collapse in={open} timeout={180} unmountOnExit>
                          <Box sx={{ position: 'sticky', left: 0, width: visibleWidth ? `${visibleWidth}px` : '100%' }}>
                            <TxnDetail t={r} />
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </Box>
      )}

      {count > PAGE && (
        <TablePagination
          component="div"
          count={count}
          page={page}
          rowsPerPage={PAGE}
          rowsPerPageOptions={[PAGE]}
          onPageChange={(_, p) => { setOpenId(null); setPage(p) }}
          sx={{ borderTop: `1px solid ${color.divider}`, color: color.muted }}
        />
      )}
    </Card>
  )
}

const visuallyHidden = {
  border: 0, clip: 'rect(0 0 0 0)', height: 1, margin: -1, overflow: 'hidden', padding: 0, position: 'absolute', whiteSpace: 'nowrap', width: 1,
}
